const CLASSIFICATION_STATUS = {
    minimal_risk: { label: "Minimal Risk", tone: "success" },
    limited_risk: { label: "Limited Risk", tone: "warning" },
    high_risk: { label: "High Risk", tone: "warning" },
    prohibited: { label: "Prohibited", tone: "rose" },
    review_required: { label: "Review Required", tone: "warning" }
};

const BASIS_LABELS = {
    ARTICLE_6_1: "Article 6(1)",
    ANNEX_III: "Annex III",
    ANNEX_III_PROFILING: "Annex III Profiling",
    ARTICLE_6_3_PROVIDER_DOCUMENTED: "Article 6(3) Provider Documented",
    article_6_1: "Article 6(1)",
    annex_iii: "Annex III",
    annex_iii_profiling: "Annex III Profiling",
    article_6_3_provider_documented: "Article 6(3) Provider Documented",
    annex_i: "Annex I",
    article_5: "Article 5",
    article_6: "Article 6"
};

export const ANNEX_III_CATEGORY_LABELS = {
    annex_iii_remote_biometric_identification:
        "Biometrics — Remote Biometric Identification",
    annex_iii_biometric_categorisation:
        "Biometrics — Biometric Categorisation",
    annex_iii_emotion_recognition: "Biometrics — Emotion Recognition",
    annex_iii_safety_component_critical_infrastructure:
        "Critical Infrastructure — Safety Component",
    annex_iii_road_traffic_management:
        "Critical Infrastructure — Road Traffic Management",
    annex_iii_water_gas_heating_electricity:
        "Critical Infrastructure — Water, Gas, Heating or Electricity",
    annex_iii_education_admission_access: "Education & Vocational Training",
    annex_iii_education_learning_outcomes: "Education & Vocational Training",
    annex_iii_education_level_assessment: "Education & Vocational Training",
    annex_iii_education_exam_monitoring: "Education & Vocational Training",
    annex_iii_employment_targeted_job_advertising:
        "Employment & Worker Management",
    annex_iii_employment_filtering_applications:
        "Employment & Worker Management",
    annex_iii_employment_ranking_candidates: "Employment & Worker Management",
    annex_iii_employment_terms_decisions: "Employment & Worker Management",
    annex_iii_employment_promotion_termination:
        "Employment & Worker Management",
    annex_iii_employment_task_allocation: "Employment & Worker Management",
    annex_iii_employment_performance_monitoring:
        "Employment & Worker Management",
    annex_iii_employment_worker_management: "Employment & Worker Management",
    annex_iii_essential_public_services_eligibility:
        "Essential Public Services",
    annex_iii_creditworthiness_scoring: "Creditworthiness / Credit Scoring",
    annex_iii_creditworthiness: "Creditworthiness / Credit Scoring",
    annex_iii_insurance_risk_pricing:
        "Life & Health Insurance Risk Assessment / Pricing",
    annex_iii_insurance: "Life & Health Insurance Risk Assessment / Pricing",
    annex_iii_emergency_triage: "Essential Public Services — Emergency Triage",
    annex_iii_le_victim_risk: "Law Enforcement",
    annex_iii_le_polygraph: "Law Enforcement",
    annex_iii_le_evidence_reliability: "Law Enforcement",
    annex_iii_le_reoffending_risk: "Law Enforcement",
    annex_iii_le_investigation_profiling: "Law Enforcement",
    annex_iii_migration_polygraph: "Migration / Asylum / Border Control",
    annex_iii_migration_risk_assessment: "Migration / Asylum / Border Control",
    annex_iii_migration_asylum_visa: "Migration / Asylum / Border Control",
    annex_iii_migration_identification: "Migration / Asylum / Border Control",
    annex_iii_justice_judicial_assistance: "Justice / Democratic Processes",
    annex_iii_justice_election_influence: "Justice / Democratic Processes"
};

export const TRANSPARENCY_TRIGGER_LABELS = {
    DIRECT_INTERACTION: "Direct interaction with natural persons",
    SYNTHETIC_CONTENT: "Generation of synthetic content",
    EMOTION_RECOGNITION: "Emotion recognition",
    BIOMETRIC_CATEGORISATION: "Biometric categorisation",
    DEEPFAKE: "Deepfake generation",
    PUBLIC_INTEREST_TEXT: "AI-generated text of public interest"
};

export const FRIA_DEPLOYER_TYPE_LABELS = {
    deployer_public_or_public_law: "Public Authority or Public-law Body",
    deployer_private_public_services: "Private Entity Providing Public Services",
    deployer_private_other: "Other Private Organisation",
    deployer_type_unknown: "Not Sure"
};

const WARNING_TITLE_LABELS = {
    ARTICLE_5_INTIMATE_UNLAWFUL_MATERIAL_QUESTIONNAIRE_GAP:
        "Article 5 intimate / unlawful material questionnaire gap",
    ARTICLE_6_3_SUBSTANTIVE_CONDITIONS_QUESTIONNAIRE_GAP:
        "Article 6(3) substantive conditions questionnaire gap",
    article_5_intimate_material_gap:
        "Article 5 intimate / unlawful material questionnaire gap",
    article_6_3_substantive_questions_gap:
        "Article 6(3) substantive conditions questionnaire gap"
};

const humanizeCode = (value) => {
    return String(value || "")
        .replace(/^annex_iii_/, "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

export const asCode = (value) => {
    if (value == null || value === "") {
        return "";
    }

    if (typeof value === "string") {
        return value;
    }

    return (
        value.frontId ||
        value.front_id ||
        value.code ||
        value.value ||
        value.key ||
        value.type ||
        value.id ||
        ""
    );
};

export const asCodeList = (items = []) => {
    return (Array.isArray(items) ? items : []).map(asCode).filter(Boolean);
};

export const labelForCode = (value, dictionary = {}) => {
    const code = asCode(value);

    if (!code) {
        return "";
    }

    return (
        dictionary[code] ||
        dictionary[String(code).toLowerCase()] ||
        dictionary[String(code).toUpperCase()] ||
        humanizeCode(code)
    );
};

export const labelsForCodes = (items, dictionary) => {
    return asCodeList(items).map((code) => labelForCode(code, dictionary));
};

const warningEntries = (warnings) => {
    if (!warnings || typeof warnings !== "object" || Array.isArray(warnings)) {
        return [];
    }

    return Object.entries(warnings).map(([key, value]) => ({
        key,
        title: labelForCode(key, WARNING_TITLE_LABELS),
        body:
            typeof value === "string"
                ? value
                : value?.message || value?.text || value?.body || ""
    }));
};

const issueTypeFromKey = (key = "") => {
    if (key.includes("hosting")) {
        return { label: "Hosting", tone: "info" };
    }

    if (key.includes("transfer") || key.includes("mismatch")) {
        return { label: "Mismatch", tone: "violet" };
    }

    if (key.includes("document")) {
        return { label: "Documentation", tone: "neutral" };
    }

    return { label: "Hosting", tone: "info" };
};

export const mapDataCategoriesDetails = (dataCategories = {}) => {
    const categories = dataCategories.categories || [];
    const count = dataCategories.count ?? categories.length;

    return {
        title: "Data Categories",
        icon: "ri-book-2-line",
        iconTone: "blue",
        subtitle: {
            key:
                count === 1
                    ? "{{count}} data category identified in this processing activity."
                    : "{{count}} data categories identified in this processing activity.",
            values: { count }
        },
        layout: "list",
        items: categories.map((category) => ({
            title: category
        })),
        emptyMessage: "No data categories identified."
    };
};

export const mapTransferHostingDetails = (transferHosting = {}) => {
    const issues = transferHosting.issues || [];
    const count = transferHosting.count ?? issues.length;

    return {
        title: "Transfer & Hosting Issues",
        icon: "ri-global-line",
        iconTone: "blue",
        subtitle: {
            key:
                count === 1
                    ? "{{count}} issue requires attention."
                    : "{{count}} issues require attention.",
            values: { count }
        },
        layout: "table",
        columns: [
            { key: "title", label: "Issue" },
            { key: "type", label: "Type" },
            { key: "status", label: "Status" }
        ],
        items: issues.map((issue) => ({
            id: issue.id || issue.key,
            title: issue.title || humanizeCode(issue.key),
            type: issueTypeFromKey(issue.key),
            status: { label: "Requires attention", tone: "warning" }
        })),
        emptyMessage: "No transfer or hosting issues detected."
    };
};

export const mapAiActClassificationDetails = (aiActAssessment = {}) => {
    const classification = aiActAssessment.classification || {};
    const statusValue = asCode(
        aiActAssessment.status || classification.status
    );
    const status = CLASSIFICATION_STATUS[statusValue] || {
        label: statusValue || "Not Assessed",
        tone: "neutral"
    };
    const basisItems = labelsForCodes(classification.basis, BASIS_LABELS).map(
        (label) => ({
            label,
            tone: "warning"
        })
    );
    const useCaseItems = labelsForCodes(
        classification.annex_iii_use_cases,
        ANNEX_III_CATEGORY_LABELS
    ).map((label) => ({
        label,
        tone: "warning"
    }));
    const reasons = classification.reasons || [];
    const reviewItems = classification.review_items || [];
    const transparency = classification.transparency || {};
    const triggerItems = labelsForCodes(
        transparency.triggers,
        TRANSPARENCY_TRIGGER_LABELS
    ).map((label) => ({
        label,
        tone: "info"
    }));
    const warnings = warningEntries(classification.warnings);
    const sections = [
        {
            title: "Status",
            type: "badges",
            items: [
                {
                    label: status.label,
                    tone: status.tone,
                    icon: "ri-shield-line"
                }
            ]
        }
    ];

    if (basisItems.length || useCaseItems.length) {
        sections.push({
            title: "Basis",
            type: "badges",
            items: [...basisItems, ...useCaseItems]
        });
    }

    if (reasons.length) {
        sections.push(
            reasons.length === 1
                ? {
                    title: "Why this basis applies",
                    type: "text",
                    literal: true,
                    text: reasons[0]
                }
                : {
                    title: "Key reasons",
                    type: "bullets",
                    literal: true,
                    items: reasons
                }
        );
    }

    if (reviewItems.length) {
        sections.push({
            title: "Review items",
            count: reviewItems.length,
            type: "bullets",
            literal: true,
            items: reviewItems
        });
    }

    if (triggerItems.length) {
        sections.push({
            title: "Transparency",
            type: "badges",
            items: triggerItems
        });
    }

    if (warnings.length) {
        sections.push({
            title: "Warnings",
            type: "warnings",
            items: warnings
        });
    }

    return {
        title: "Classification Summary",
        icon: "ri-shield-check-line",
        iconTone: "success",
        layout: "sections",
        sections,
        emptyMessage: "No classification details are available yet."
    };
};
